import { JSX, useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

// ─── Constants ──────────────────────────────────────────────────────────────

const ITEM_HEIGHT = 44; // height of the selected (center) row in px
const VISIBLE_ROWS = 5;
const CONTAINER_HEIGHT = ITEM_HEIGHT * VISIBLE_ROWS; // 220px
// Pad top/bottom so the first and last items can scroll to center.
// Center of container = 220/2 = 110px. Center of item[0] with no padding = 22px.
// Padding needed = 110 - 22 = 88px = 2 * ITEM_HEIGHT.
const COLUMN_PADDING = ITEM_HEIGHT * 2;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS: number[] = Array.from(
  { length: CURRENT_YEAR - 1900 + 11 },
  (_, i) => 1900 + i
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDaysInMonth(month: number, year: number): number {
  // month is 0-based
  return new Date(year, month + 1, 0).getDate();
}

function parseISODate(value: string): {
  year: number;
  month: number;
  day: number;
} {
  if (value) {
    const parts = value.split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      return { year: parts[0], month: parts[1] - 1, day: parts[2] };
    }
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
}

function toISODate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
}

// ─── WheelColumn ─────────────────────────────────────────────────────────────

interface WheelColumnProps {
  items: (string | number)[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

function WheelColumn({
  items,
  selectedIndex,
  onSelect,
}: WheelColumnProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const isUserScrolling = useRef(false);
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll to the selected item when it changes programmatically.
  useEffect(() => {
    if (!containerRef.current || isUserScrolling.current) {
      return;
    }
    containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
  }, [selectedIndex]);

  // On mount, set scroll position immediately (no transition).
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
    }
    // Run only on mount — selectedIndex changes are handled above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(() => {
    isUserScrolling.current = true;
    if (scrollTimer.current) {
      clearTimeout(scrollTimer.current);
    }
    scrollTimer.current = setTimeout(() => {
      isUserScrolling.current = false;
      if (!containerRef.current) {
        return;
      }
      const rawIndex = containerRef.current.scrollTop / ITEM_HEIGHT;
      const snappedIndex = Math.max(
        0,
        Math.min(items.length - 1, Math.round(rawIndex))
      );
      // Snap scroll position to the nearest item.
      containerRef.current.scrollTop = snappedIndex * ITEM_HEIGHT;
      onSelect(snappedIndex);
    }, 120);
  }, [items.length, onSelect]);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        height: CONTAINER_HEIGHT,
        overflowY: 'scroll',
        scrollSnapType: 'y mandatory',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        pt: `${COLUMN_PADDING}px`,
        pb: `${COLUMN_PADDING}px`,
      }}
    >
      {items.map((item, idx) => {
        const dist = Math.abs(idx - selectedIndex);
        const isSelected = dist === 0;
        const isAdjacent = dist === 1;
        return (
          <Box
            key={idx}
            onClick={() => onSelect(idx)}
            sx={{
              scrollSnapAlign: 'center',
              height: ITEM_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: isSelected ? '10px' : 0,
              backgroundColor: isSelected ? 'action.selected' : 'transparent',
              fontSize: isSelected ? 17 : 15,
              fontWeight: isSelected ? 700 : 'normal',
              color: isSelected
                ? 'text.primary'
                : isAdjacent
                ? 'text.disabled'
                : 'grey.400',
              cursor: 'pointer',
              userSelect: 'none',
              mx: 1,
            }}
          >
            {item}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── DrumPicker ───────────────────────────────────────────────────────────────

interface Props {
  value: string; // ISO "YYYY-MM-DD" or ""
  onChange: (v: string) => void;
}

export default function DrumPicker({ value, onChange }: Props): JSX.Element {
  const { year, month, day } = parseISODate(value);

  // Seed onChange with today when the field is empty so the parent has a value
  // even if the user doesn't touch the drum (a drum always shows something).
  useEffect(() => {
    if (!value) {
      const now = new Date();
      onChange(toISODate(now.getFullYear(), now.getMonth(), now.getDate()));
    }
    // Run only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daysInMonth = getDaysInMonth(month, year);
  const days: number[] = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const yearIndex = YEARS.indexOf(year);
  // Clamp day index in case the month/year change reduces the number of days.
  const dayIndex = Math.min(day - 1, daysInMonth - 1);

  const handleMonthSelect = useCallback(
    (idx: number) => {
      const newDaysInMonth = getDaysInMonth(idx, year);
      const clampedDay = Math.min(day, newDaysInMonth);
      onChange(toISODate(year, idx, clampedDay));
    },
    [day, year, onChange]
  );

  const handleDaySelect = useCallback(
    (idx: number) => {
      onChange(toISODate(year, month, idx + 1));
    },
    [year, month, onChange]
  );

  const handleYearSelect = useCallback(
    (idx: number) => {
      const newYear = YEARS[idx];
      const newDaysInMonth = getDaysInMonth(month, newYear);
      const clampedDay = Math.min(day, newDaysInMonth);
      onChange(toISODate(newYear, month, clampedDay));
    },
    [day, month, onChange]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        py: '12px',
      }}
    >
      <WheelColumn
        items={MONTHS}
        selectedIndex={month}
        onSelect={handleMonthSelect}
      />
      <WheelColumn
        items={days}
        selectedIndex={dayIndex}
        onSelect={handleDaySelect}
      />
      <WheelColumn
        items={YEARS}
        selectedIndex={yearIndex >= 0 ? yearIndex : YEARS.indexOf(CURRENT_YEAR)}
        onSelect={handleYearSelect}
      />
    </Box>
  );
}
